import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { SourcingService, SourcingSetting } from 'api-clients/monolith';
import clsx from 'clsx';
import { Info } from 'lucide-react';
import { Suspense } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { updateSourcingSettings } from '@/api-clients/update-sourcing-settings';
import { useToaster } from '@/components/base/toaster';
import { DEFAULT_METRICS_PERIOD_OPTIONS } from '@/components/pages/settings/dashboard/constants';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

interface DashboardSettingsContentProps {
  selectedDays?: number | null;
  onSelectDays?: (days: number | null) => void;
  disabled?: boolean;
}

function DashboardSettingsContent({
  selectedDays,
  onSelectDays,
  disabled = false,
}: DashboardSettingsContentProps) {
  const controlsDisabled = disabled;

  return (
    <div className="mt-4 flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Typography variant="h4" as="h1">
          <FormattedMessage
            defaultMessage="Dashboard"
            description="Title of the dashboard settings tab"
          />
        </Typography>
        <Typography variant="body1" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Manage sourcing dashboard"
            description="Subtitle of the dashboard settings tab"
          />
        </Typography>
      </div>

      <div className="border-fx-neutral-200 rounded-lg border bg-white">
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <Typography variant="medium2" as="h2">
              <FormattedMessage
                defaultMessage="Default metrics period for openings without hiring goals"
                description="Section title for default metrics period setting"
              />
            </Typography>
            <Typography variant="body1" className="!text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="When an opening has no hiring goal set, metrics will be calculated based on this time period by default. This applies to the Dashboard view and opening details pages."
                description="Explains default metrics period for openings without hiring goals"
              />
            </Typography>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="body1">
              <FormattedMessage
                defaultMessage="Show metrics for:"
                description="Label before metrics period options"
              />
            </Typography>
            <div className="flex flex-wrap gap-3">
              {DEFAULT_METRICS_PERIOD_OPTIONS.map(days => {
                const selected = selectedDays === days;
                return (
                  <Button
                    key={days}
                    variant="outline"
                    disabled={controlsDisabled}
                    onClick={() => onSelectDays?.(days ?? null)}
                    className={clsx(
                      'leading-5',
                      selected
                        ? 'border-fx-brand-600 bg-fx-brand-50 text-fx-brand-600'
                        : 'text-fx-neutral-600 hover:bg-fx-neutral-50 bg-white font-normal',
                    )}
                  >
                    {days !== undefined && days !== null ? (
                      <FormattedMessage
                        defaultMessage="Last {days} days"
                        description="Label for metrics period option"
                        values={{ days }}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <FormattedMessage
                          defaultMessage="Lifetime"
                          description="Label for metrics period option"
                        />
                        <Tooltip
                          content={
                            <FormattedMessage
                              defaultMessage="Shows all performance data since opening was created."
                              description="Tooltip on metrics period option: lifetime default when org metrics period is lifetime"
                            />
                          }
                        >
                          <Info
                            className="text-fx-neutral-600 size-4 shrink-0"
                            aria-hidden
                          />
                        </Tooltip>
                      </div>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Info className="text-fx-neutral-600 size-4 shrink-0" aria-hidden />
            <Typography
              variant="small1"
              className="!text-fx-neutral-900 min-w-0 flex-1"
            >
              <FormattedMessage
                defaultMessage="This setting only affects openings without hiring goals. Openings with hiring goals will continue to show metrics based on their goal's start and end dates."
                description="Footnote for default metrics period setting"
              />
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardSettingsWrapper() {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery<SourcingSetting>({
    queryKey: queryKeys.settings.automations,
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingSettings() as Promise<SourcingSetting>,
  });

  const selectedDays = data.default_metrics_period;

  const { mutate: updateSettings, isPending } = useMutation<
    SourcingSetting,
    Error,
    Partial<SourcingSetting>,
    { previousSettings: SourcingSetting | undefined }
  >({
    mutationFn: updateSourcingSettings,
    onMutate: variables => {
      const previousSettings = queryClient.getQueryData<SourcingSetting>(
        queryKeys.settings.automations,
      );
      if (previousSettings) {
        queryClient.setQueryData<SourcingSetting>(
          queryKeys.settings.automations,
          {
            ...previousSettings,
            default_metrics_period: variables.default_metrics_period ?? null,
          },
        );
      }
      return { previousSettings };
    },
    onSuccess: response => {
      queryClient.setQueryData<SourcingSetting>(
        queryKeys.settings.automations,
        response,
      );
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Settings updated successfully!',
          description: 'Success message for updating sourcing settings',
        }),
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData<SourcingSetting>(
          queryKeys.settings.automations,
          context.previousSettings,
        );
      }
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update settings!',
          description: 'Error message for updating sourcing settings',
        }),
      });
    },
  });

  return (
    <DashboardSettingsContent
      selectedDays={selectedDays}
      onSelectDays={days => {
        if (days === selectedDays) {
          return;
        }
        updateSettings({ default_metrics_period: days });
      }}
      disabled={isPending}
    />
  );
}

export function DashboardSettings() {
  return (
    <Suspense fallback={<DashboardSettingsContent />}>
      <DashboardSettingsWrapper />
    </Suspense>
  );
}
